import { useCallback, useEffect, useRef, useState } from "react";
import { createLruCache } from "./lruCache";

// Loops a single preview audio file entirely inside the Web Audio graph,
// instead of an <audio> element. An <audio> element cannot loop gaplessly
// even given a perfectly sample-exact file (see the backend's preview.ogg,
// embedded by the plugin's own offline render): its native
// `loop` attribute, and any JS that wraps currentTime by hand, are both
// scheduled on the MEDIA ELEMENT's clock, which the spec does not guarantee
// to be sample-accurate. An AudioBufferSourceNode with loop=true instead
// schedules the wrap INSIDE the audio graph itself - sample-accurate by
// construction, so there is no gap and no drift, ever.
//
// This hook is designed to be driven by a changing `url`, so it can be
// owned in two different ways:
//  - KitDetail calls it with a fixed url for the life of the page: one
//    AudioContext, one thing to ever play, `toggle()` flips it.
//  - KitsCatalogue owns ONE instance for the whole grid and re-points `url`
//    at whichever card's preview the visitor last clicked - a single shared
//    AudioContext, never one per card (browsers cap how many can be live at
//    once, and two cards playing at the same time would defeat the point of
//    "only one preview plays at a time"). See its own url-change effect
//    below for how switching stops the previous card's audio.
//
// Isolated here (mirrors how patternGeometry.js isolates the visualiser's
// pure geometry) so KitDetail.jsx stays readable and this is unit-testable
// in isolation from the component tree.

// Decoded PCM is far bigger than the compressed download (16-bit/22050Hz
// mono WAV, already uncompressed, expands further once decodeAudioData
// hands back 32-bit float samples) - caching every preview a visitor
// auditions across a 34-item catalogue would grow without bound. A couple
// of entries is enough to make flipping back to a just-played card instant,
// without holding the whole catalogue in memory.
const BUFFER_CACHE_SIZE = 2;
// Ceiling on resume(): see resumeBounded.
const RESUME_TIMEOUT_MS = 750;

export function useLoopPlayer(url) {
  const audioCtxRef = useRef(null);
  const cacheRef = useRef(null); // lazy: created alongside the AudioContext
  const inFlightRef = useRef(new Map()); // url -> in-flight fetch+decode promise,
                                          // so a second click on the SAME url
                                          // while it's loading awaits the one
                                          // fetch instead of starting another
  const sourceRef = useRef(null);
  const sourceUrlRef = useRef(null); // which url the live source node is for
  const startedAtRef = useRef(0); // AudioContext time playback began
  const startingUrlRef = useRef(null); // url currently mid-play(), if any -
                                        // guards a double-click on the SAME
                                        // card from firing two overlapping
                                        // play() calls, WITHOUT blocking a
                                        // switch to a DIFFERENT url while one
                                        // is still loading
  // Bumped on every event that must invalidate whatever play() call is
  // currently mid-flight: an explicit stop(), the url changing to something
  // else, and unmounting. Each play() call snapshots the value at the start
  // (`myGen`) and re-checks it after every `await` - if it no longer
  // matches, this exact call has been superseded and must not touch
  // sourceRef/audioCtxRef or flip isPlaying/isLoading, no matter what the
  // fetch+decode it was waiting on eventually resolves with.
  //
  // This replaces an earlier version that only compared against the latest
  // `url` (a ref kept in sync by the effect below). That caught a switch to
  // a DIFFERENT card, but not the two other cases this hook explicitly
  // needs to guard against: pausing the SAME url back to idle while its own
  // decode is still in flight, and unmounting entirely. Neither of those
  // used to touch the url comparison at all, so a slow decode that outlived
  // either one would sail through the stale check (the url it captured
  // never stopped matching) and start playing anyway - on unmount this
  // recreated a brand new AudioContext from scratch (ensureContext() saw
  // audioCtxRef.current already nulled out by the unmount cleanup below),
  // producing an orphaned, looping source with no component, button, or
  // spacebar listener left anywhere that could ever call stop() on it.
  const genRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Stops and releases the current source node. A stopped
  // AudioBufferSourceNode can never be restarted (per the Web Audio spec),
  // so play() always builds a fresh one - this only ever tears one down.
  //
  // Also cancels a load that hasn't produced a source yet: bumping genRef
  // means a decode already in flight for this instance will see itself as
  // superseded when it resolves and bail out instead of starting, and
  // clearing startingUrlRef/isLoading means the UI doesn't stay stuck
  // showing "loading" for a play() that is now guaranteed to no-op.
  const stop = useCallback(() => {
    genRef.current += 1;
    const source = sourceRef.current;
    if (source) {
      sourceRef.current = null;
      sourceUrlRef.current = null;
      try {
        source.stop();
      } catch {
        // Already stopped, or never started - stopping twice must not throw.
      }
      try {
        source.disconnect();
      } catch {
        // ignore
      }
    }
    startingUrlRef.current = null;
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const ensureContext = useCallback(() => {
    // The cache is no longer tied to the context's lifetime: an AudioBuffer is
    // not bound to the context that decoded it, so it survives a rebuild.
    if (!cacheRef.current) {
      cacheRef.current = createLruCache(BUFFER_CACHE_SIZE);
    }
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        throw new Error("This browser does not support Web Audio playback.");
      }
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  }, []);

  // resume() with a ceiling, and trust `state` afterwards rather than the
  // promise. WebKit parks the resume promise -- never resolving, never
  // rejecting -- when the page is not allowed to play, so awaiting it
  // unbounded would leave isLoading true forever.
  const resumeBounded = useCallback(async (ctx) => {
    let timer;
    const ceiling = new Promise((resolve) => {
      timer = setTimeout(resolve, RESUME_TIMEOUT_MS);
    });
    try {
      await Promise.race([Promise.resolve(ctx.resume()).catch(() => {}), ceiling]);
    } finally {
      clearTimeout(timer);
    }
    return ctx.state === "running";
  }, []);

  // Close a context we no longer trust and hand back a fresh one. Cheap: the
  // decoded buffers stay in cacheRef, and the new context inherits the click
  // that brought us here.
  const rebuildContext = useCallback(() => {
    const old = audioCtxRef.current;
    audioCtxRef.current = null;
    if (old && old.state !== "closed") {
      old.close().catch(() => {});
    }
    return ensureContext();
  }, [ensureContext]);

  // Fetches+decodes targetUrl into an AudioBuffer, or returns it straight
  // from the small LRU cache. Both the AudioContext and the fetch must be
  // reachable from a user gesture - the play button's click handler calls
  // this synchronously via play() - or the browser's autoplay policy leaves
  // the context suspended forever.
  const ensureBuffer = useCallback(
    async (targetUrl) => {
      const ctx = ensureContext();
      const cached = cacheRef.current.get(targetUrl);
      if (cached) return cached;
      const inFlight = inFlightRef.current.get(targetUrl);
      if (inFlight) return inFlight;
      const promise = (async () => {
        const res = await fetch(targetUrl);
        if (!res.ok) {
          throw new Error(`Could not load the preview audio (${res.status}).`);
        }
        const arrayBuffer = await res.arrayBuffer();
        // The callback form (not the promise form) of decodeAudioData is the
        // widest-compatible one across the browsers this site supports.
        const buffer = await new Promise((resolve, reject) => {
          ctx.decodeAudioData(arrayBuffer, resolve, reject);
        });
        cacheRef.current?.set(targetUrl, buffer);
        return buffer;
      })();
      inFlightRef.current.set(targetUrl, promise);
      try {
        return await promise;
      } finally {
        inFlightRef.current.delete(targetUrl);
      }
    },
    [ensureContext]
  );

  const play = useCallback(async () => {
    const targetUrl = url;
    if (!targetUrl) {
      setError("No preview audio to play yet.");
      return;
    }
    // Already playing (or already starting) exactly this url: a no-op, not
    // a restart. Keyed on the url rather than a plain boolean so switching
    // to a DIFFERENT url while this one is still loading is never blocked.
    if (startingUrlRef.current === targetUrl || sourceUrlRef.current === targetUrl) {
      return;
    }
    // Snapshot the current generation. stop()/a url change/unmount bump
    // genRef - if that happens while this call is suspended below, every
    // check against `myGen` fails and this call quietly gives up instead of
    // touching state that no longer belongs to it.
    const myGen = genRef.current;
    startingUrlRef.current = targetUrl;
    setError("");
    setIsLoading(true);
    try {
      const buffer = await ensureBuffer(targetUrl);
      if (genRef.current !== myGen) return; // superseded while loading
      let ctx = ensureContext();
      if (ctx.state !== "running") {
        // Anything that is not "running" needs resume(), not just
        // "suspended". Safari has a fourth state, "interrupted", which the OS
        // imposes when it takes the audio session away -- system sleep is the
        // one that bites on macOS. The clock is frozen exactly as in
        // "suspended", but the old `=== "suspended"` test was false for it, so
        // resume() never fired and start() played into a stopped destination:
        // the button said Pause and nothing came out, for the life of the tab.
        // This click is the user gesture that lets resume() through.
        let running = await resumeBounded(ctx);
        if (genRef.current !== myGen) return; // superseded during resume()
        if (!running) {
          // resume() did not take. WebKit can leave a context wedged in
          // "interrupted" when it fails to re-acquire the audio session, and a
          // fresh context is the documented way out.
          ctx = rebuildContext();
          running = ctx.state === "running" || (await resumeBounded(ctx));
          if (genRef.current !== myGen) return;
          if (!running) {
            throw new Error("Safari paused audio for this tab. Press play again.");
          }
        }
      }

      // Belt-and-suspenders: only one source ever plays at a time. In the
      // catalogue this is normally already true by the time we get here
      // (switching `url` stops the previous source synchronously - see the
      // effect below - and that always happens-before this async
      // continuation resumes), but tearing down defensively here means that
      // invariant does not depend on effect ordering to hold.
      const previous = sourceRef.current;
      if (previous) {
        sourceRef.current = null;
        sourceUrlRef.current = null;
        previous.onended = null;
        try { previous.stop(); } catch { /* already stopped */ }
        try { previous.disconnect(); } catch { /* ignore */ }
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.loopStart = 0;
      source.loopEnd = buffer.duration;
      source.connect(ctx.destination);
      source.onended = () => {
        // Fires on an explicit stop() too, but stop() has already cleared
        // sourceRef.current to null by the time this callback runs - only
        // reconcile state if THIS source is still the active one, so a
        // stale onended from a previous source can't clobber a newer one.
        if (sourceRef.current === source) {
          sourceRef.current = null;
          sourceUrlRef.current = null;
          setIsPlaying(false);
        }
      };
      // Only reached once every staleness check above has passed in the
      // SAME synchronous stretch of code (no more awaits from here on), so
      // there is no window left in which a concurrent stop()/switch/unmount
      // could sneak in between "decided to start" and "actually started":
      // this is the one moment isPlaying/getPosition are allowed to start
      // reporting motion, and it is also the moment the sound itself starts.
      source.start(0);
      sourceRef.current = source;
      sourceUrlRef.current = targetUrl;
      startedAtRef.current = ctx.currentTime;
      setIsPlaying(true);
    } catch (err) {
      if (genRef.current === myGen) {
        setError(err?.message || "Could not play the preview.");
        setIsPlaying(false);
      }
    } finally {
      if (startingUrlRef.current === targetUrl) startingUrlRef.current = null;
      if (genRef.current === myGen) setIsLoading(false);
    }
  }, [url, ensureBuffer, ensureContext, resumeBounded, rebuildContext]);

  const toggle = useCallback(() => {
    if (sourceUrlRef.current === url && isPlaying) {
      stop();
    } else if (startingUrlRef.current === url) {
      // A decode for this exact url is still in flight: treat "pause" as
      // "cancel the load", not "start over". Without this branch, toggle()
      // would fall through to play(), which no-ops against its own
      // in-flight guard above - leaving the user with no way to call off a
      // slow load once it has started, and isLoading stuck true until it
      // resolves on its own.
      stop();
    } else {
      play();
    }
  }, [isPlaying, url, play, stop]);

  // Stable identity (empty deps - everything it reads is a ref, never
  // component state) so a caller's rAF-driving effect (PluginWindow)
  // can depend on it without re-subscribing on every render.
  const getPosition = useCallback(() => {
    const ctx = audioCtxRef.current;
    const source = sourceRef.current;
    const duration = source?.buffer?.duration;
    if (!ctx || !source || !(duration > 0)) return 0;
    const elapsed = ctx.currentTime - startedAtRef.current;
    // Wrap into [0, duration), matching patternGeometry.playheadX's own
    // guard - defensive only, elapsed should never go negative here since
    // ctx.currentTime is monotonic and startedAtRef is set from it.
    return ((elapsed % duration) + duration) % duration;
  }, []);

  // Loop length of what is playing now, 0 when nothing is. Stable identity
  // for the same reason as getPosition.
  const getDuration = useCallback(() => sourceRef.current?.buffer?.duration || 0, []);

  // Whenever the preview URL changes (a card switch in the catalogue, or -
  // more rarely - the detail page's own :id changing while it stays
  // mounted), stop whatever is currently playing so the OLD url's audio
  // never keeps looping under the NEW selection. This is what makes "only
  // one preview plays at a time" hold even before play() for the new url
  // has run: it always runs synchronously, so it happens-before any
  // in-flight play() call's async continuation can resume and touch
  // sourceRef. Decoded buffers are deliberately NOT dropped here - they
  // stay in the small LRU cache (see ensureBuffer) so switching back to a
  // recently-played url doesn't refetch it.
  useEffect(() => {
    return () => {
      // Invalidate whatever generation was in flight for the url this
      // effect instance was for - this cleanup runs BOTH when `url` is
      // about to change to something else AND on true unmount, which is
      // exactly the pair of cases a decode in flight must not survive.
      genRef.current += 1;
      const source = sourceRef.current;
      sourceRef.current = null;
      sourceUrlRef.current = null;
      if (source) {
        try { source.stop(); } catch { /* already stopped */ }
        try { source.disconnect(); } catch { /* ignore */ }
      }
      setIsPlaying(false);
    };
  }, [url]);

  // Separately, only on true unmount: release the AudioContext itself, so a
  // played preview doesn't keep an output stream open after navigating away
  // (e.g. the catalogue unmounting because the visitor opened a kit, or
  // navigated to a different page entirely).
  useEffect(() => {
    // Snapshot the Map now: it's never reassigned (always mutated in place),
    // but the cleanup below runs later, and reading a ref's `.current`
    // inside a cleanup is what react-hooks/exhaustive-deps warns about.
    const inFlight = inFlightRef.current;
    return () => {
      const ctx = audioCtxRef.current;
      audioCtxRef.current = null;
      cacheRef.current = null;
      inFlight.clear();
      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => {});
      }
    };
  }, []);

  return { isPlaying, isLoading, error, play, stop, toggle, getPosition, getDuration };
}
